import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { IndianRupee, TrendingUp, Wallet, CreditCard, Smartphone, Banknote, Receipt } from "lucide-react";

const RANGES = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "month", label: "This month" },
];

function formatINR(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function StatCard({ label, value, Icon, tint, testid, sub }) {
  return (
    <div
      className="card-soft card-hover p-6 sm:p-7"
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-taupe-500">{label}</div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tint}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <div className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight text-espresso-900">
        {formatINR(value)}
      </div>
      {sub && <div className="text-xs text-taupe-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/analytics/summary`, { params: { range } });
        if (!cancel) setData(res.data);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [range]);

  const totals = data?.totals || {};
  const series = data?.series || [];
  const profit = (totals.total_sales || 0) - (totals.total_expenses || 0);

  return (
    <div className="p-6 sm:p-10 max-w-[1400px] mx-auto animate-fade-in-up" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-taupe-500 mb-2">Overview</div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tighter text-espresso-900">
            Dashboard
          </h1>
          <p className="text-taupe-600 mt-2 text-sm">A warm summary of your cafe&apos;s numbers.</p>
        </div>
        <div className="inline-flex bg-white border border-taupe-200 rounded-xl p-1 shadow-sm">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              data-testid={`dashboard-range-${r.value}`}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                range === r.value
                  ? "bg-espresso-800 text-white"
                  : "text-espresso-800 hover:bg-taupe-100"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <StatCard
          testid="stat-total-sales"
          label="Total Sales"
          value={totals.total_sales}
          Icon={TrendingUp}
          tint="bg-matcha-100 text-matcha-500"
        />
        <StatCard
          testid="stat-daily-sales"
          label="Today's Sales"
          value={totals.todays_sales}
          Icon={IndianRupee}
          tint="bg-espresso-100 text-espresso-800"
        />
        <StatCard
          testid="stat-total-expenses"
          label="Total Expenses"
          value={totals.total_expenses}
          Icon={Receipt}
          tint="bg-terracotta-100 text-terracotta-500"
        />
        <StatCard
          testid="stat-daily-expenses"
          label="Today's Expenses"
          value={totals.todays_expenses}
          Icon={Wallet}
          tint="bg-taupe-100 text-taupe-700"
        />
      </div>

      {/* Chart */}
      <div className="card-soft p-6 sm:p-8 mb-8" data-testid="dashboard-chart-card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-taupe-500 mb-1">Trend</div>
            <h2 className="font-heading font-bold text-xl sm:text-2xl tracking-tight text-espresso-900">
              Sales vs Expenses
            </h2>
          </div>
          <div className="flex items-center gap-4 text-xs text-taupe-600">
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-matcha-500 inline-block" />Sales</span>
            <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-terracotta-500 inline-block" />Expenses</span>
          </div>
        </div>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <AreaChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#4A5D23" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#4A5D23" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#C05A46" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#C05A46" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#EAE6E1" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke="#A39A94" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#A39A94" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(v) => `₹${v}`} />
              <Tooltip
                contentStyle={{ borderRadius: 12, border: "1px solid #EAE6E1", boxShadow: "0 12px 32px rgba(44,36,33,0.08)" }}
                formatter={(v) => formatINR(v)}
              />
              <Area type="monotone" dataKey="sales" stroke="#4A5D23" strokeWidth={2.5} fill="url(#salesGrad)" />
              <Area type="monotone" dataKey="expenses" stroke="#C05A46" strokeWidth={2.5} fill="url(#expGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Payment split */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard testid="stat-cash-sales" label="Cash Sales" value={totals.cash_sales} Icon={Banknote} tint="bg-matcha-100 text-matcha-500" />
        <StatCard testid="stat-upi-sales" label="UPI Sales" value={totals.upi_sales} Icon={Smartphone} tint="bg-espresso-100 text-espresso-800" />
        <StatCard testid="stat-card-sales" label="Card Sales" value={totals.card_sales} Icon={CreditCard} tint="bg-taupe-100 text-taupe-700" />
        <StatCard testid="stat-online-sales" label="Online Sales" value={totals.online_sales} Icon={Smartphone} tint="bg-terracotta-100 text-terracotta-500" />
      </div>

      <div className="mt-6 text-sm text-taupe-600">
        Estimated profit (all-time):{" "}
        <span className={`font-semibold ${profit >= 0 ? "text-matcha-500" : "text-terracotta-500"}`} data-testid="dashboard-profit">
          {formatINR(profit)}
        </span>
      </div>

      {loading && <div className="mt-4 text-xs text-taupe-500">Refreshing…</div>}
    </div>
  );
}
